import { useBuilderApiClient } from '@/api/builder/useBuilderApiClient'
import { useWorkspace } from '@/modules/workspaces/composables/useWorkspace'
import type { ApiPaginationResponse } from '@/types/api/dashboard'
import type { PostWorkflowRunPayload, PostWorkflowRunPayloadV3, RunDetails, RunResults, RunsResponse, RunsStatsResponse, RunTask, WorkflowExecutionResponse } from './types'

export const ENDPOINTS = {
    RUNS: '/external-dashboard/workspaces/:workspaceId/workflows/executions',
    RUNS_STATS: '/external-dashboard/workspaces/:workspaceId/workflows/executions/stats',
    RUN_DETAILS: '/external-dashboard/workspaces/:workspaceId/workflows/executions/:runId',
    RUN_TASKS: '/external-dashboard/workspaces/:workspaceId/workflows/executions/:workflowId/tasks',
    RUN_CANCEL: '/external-dashboard/workspaces/:workspaceId/workflows/:runId/cancel',
    RUN_RESULTS: '/external-dashboard/workspaces/:workspaceId/workflows/:runId/result',
    WORKFLOWS_RUN: '/external-dashboard/workspaces/:workspaceId/workflows/run',
    WORKFLOW_RUN: '/external-dashboard/workspaces/:workspaceId/workflows/:workflowId',
    WORKFLOWS: '/external-dashboard/workspaces/:workspaceId/workflows',
}

export function fetchRuns() {
    const { currentWorkspaceId } = useWorkspace()
    return useBuilderApiClient<RunsResponse>({
        url: ENDPOINTS.RUNS,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
        },
        method: 'GET',
        headers: { accept: 'application/json;version=2' },
    })
}

export function fetchStats() {
    const { currentWorkspaceId } = useWorkspace()
    return useBuilderApiClient<RunsStatsResponse>({
        url: ENDPOINTS.RUNS_STATS,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
        },
        method: 'GET',
        headers: { accept: 'application/json;version=2' },
    })
}

export function fetchRunDetails(id: string) {
    const { currentWorkspaceId } = useWorkspace()
    const { call, isLoading } = useBuilderApiClient<RunDetails>({
        url: ENDPOINTS.RUN_DETAILS,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
            runId: id,
        },
        method: 'GET',
        headers: { accept: 'application/json;version=2' },
    })

    return {
        isLoading,
        call: () => {
            return call()
                .then(response => {
                    const { data } = response || {}
                    return data
                })
        },
    }
}

export function runWorkflow(params: { workspaceId: string }, data: PostWorkflowRunPayload) {
    return useBuilderApiClient<WorkflowExecutionResponse>({
        url: ENDPOINTS.WORKFLOWS_RUN,
        method: 'POST',
        urlParams: params,
        data: {
            ...data,
        },
    })
}

export function runWorkflowV3(params: { workspaceId: string }, data: PostWorkflowRunPayloadV3) {
    return useBuilderApiClient<WorkflowExecutionResponse>({
        method: 'POST',
        url: ENDPOINTS.WORKFLOWS,
        urlParams: {
            workspaceId: params.workspaceId as string,
        },
        data,
    })
}

export function getWorkflowExecutionStatus(params: { workspaceId: string }) {
    return useBuilderApiClient<WorkflowExecutionResponse>({
        url: ENDPOINTS.WORKFLOW_RUN,
        method: 'GET',
        urlParams: params,
        headers: { accept: 'application/json;version=2' },
    })
}

export function fetchRunResults(id: string) {
    const { currentWorkspaceId } = useWorkspace()
    return useBuilderApiClient<RunResults>({
        url: ENDPOINTS.RUN_RESULTS,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
            runId: id,
        },
        method: 'GET',
        headers: { accept: 'application/json;version=2' },
    })
}

export function cancelWorkflow(id: string) {
    const { currentWorkspaceId } = useWorkspace()
    return useBuilderApiClient({
        url: ENDPOINTS.RUN_CANCEL,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
            runId: id,
        },
        method: 'POST',
    })
}

export function fetchWorkflowTasks(workflowId: string) {
    const { currentWorkspaceId } = useWorkspace()
    return useBuilderApiClient<ApiPaginationResponse<RunTask[]>>({
        url: ENDPOINTS.RUN_TASKS,
        urlParams: {
            workspaceId: currentWorkspaceId.value!,
            workflowId,
        },
        method: 'GET',
        headers: { accept: 'application/json;version=2' },
    })
}
